import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, Mail, RotateCcw } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { ChampFiltre } from '../components/ui/Onglets';
import { BandeauExpedition } from '../components/notifications/BandeauExpedition';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import {
  urlPdfSynthese,
  useEnvoyerSynthese,
  useGenererSynthese,
  useSyntheseMensuelle,
  useSynthesesMensuelles,
  type ContenuSynthese,
  type IndicateurSynthese,
  type ListeSyntheses,
  type SectionSynthese,
  type TableauSynthese,
  type TonEcart,
  type VueSynthese,
} from '../lib/useSynthesesMensuelles';

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function jourFrancais(jour: string): string {
  const [annee, mois, j] = jour.split('-');
  return `${j}/${mois}/${annee}`;
}

function horodatage(instant: string): string {
  return new Date(instant).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

function auteur(synthese: VueSynthese): string {
  return synthese.origine === 'AUTOMATIQUE' ? 'automatiquement' : `par ${synthese.genereePar ?? 'un compte retiré'}`;
}

/**
 * Synthèses mensuelles de la direction (phase 5, TF-440). Le 1er du mois, la
 * plateforme produit la synthèse du mois écoulé — activité, satisfaction,
 * absentéisme, financeurs — et l'envoie aux comptes de la direction ; elle se
 * relit ici, telle qu'elle a été figée. La direction génère un mois passé ou
 * une nouvelle version, et renvoie une synthèse par e-mail.
 *
 * L'écran ne décide rien : textes, écarts et tons viennent de
 * `/api/syntheses-mensuelles`.
 */
export function DirSynthesesPage(): JSX.Element {
  const [parametres, setParametres] = useSearchParams();
  const liste = useSynthesesMensuelles();
  const [generationOuverte, setGenerationOuverte] = useState(false);

  const toutes = liste.data?.mois.flatMap((mois) => mois.versions) ?? [];
  const demandee = parametres.get('synthese');
  const choisie = toutes.find((synthese) => synthese.id === demandee) ?? toutes[0] ?? null;

  const choisir = (id: string): void => {
    const suivants = new URLSearchParams(parametres);
    suivants.set('synthese', id);
    setParametres(suivants, { replace: true });
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Synthèses mensuelles"
        sousTitre="Activité, satisfaction, absentéisme, financeurs et impact économique de chaque mois, produits le 1er et envoyés à la direction"
        actions={
          liste.data && (
            <BoutonSecondaire onClick={() => setGenerationOuverte(true)}>Générer une synthèse</BoutonSecondaire>
          )
        }
      />

      {liste.isLoading && <Chargement libelle="Chargement des synthèses…" />}
      {liste.error && <MessageErreur message={messageErreur(liste.error, 'Impossible de charger les synthèses.')} />}

      {liste.data && (
        <>
          <Prochaine liste={liste.data} />
          {!liste.data.expedition.email && (
            <BandeauExpedition
              etat={liste.data.expedition}
              contexte="Les synthèses restent consultables ici et dans « Mes notifications »."
            />
          )}

          {toutes.length === 0 ? (
            <div className="rounded-2xl bg-white p-6 text-sm text-codialis-gray shadow-card">
              Aucune synthèse pour l’instant. La première sera produite le{' '}
              <strong className="text-codialis-navy">{jourFrancais(liste.data.prochaine.le)}</strong> pour{' '}
              {liste.data.prochaine.titre.toLowerCase()}. Un mois passé se génère avec « Générer une synthèse ».
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
              <ListeMois liste={liste.data} choisie={choisie} onChoisir={choisir} />
              {choisie && <Synthese synthese={choisie} onChoisir={choisir} />}
            </div>
          )}
        </>
      )}

      {generationOuverte && liste.data && (
        <ModaleGeneration
          liste={liste.data}
          onFermer={() => setGenerationOuverte(false)}
          onGeneree={(synthese) => {
            setGenerationOuverte(false);
            choisir(synthese.id);
          }}
        />
      )}
    </div>
  );
}

function Prochaine({ liste }: { liste: ListeSyntheses }): JSX.Element {
  return (
    <div className="rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy sm:p-5">
      Prochaine synthèse automatique : <strong>{liste.prochaine.titre}</strong>, le{' '}
      <strong>{jourFrancais(liste.prochaine.le)}</strong> à 6 h, envoyée par e-mail et dans l’application{' '}
      {liste.destinataires.length === 0 ? (
        <span className="font-semibold text-red-700">— aucun compte de la direction n’est actif.</span>
      ) : (
        <>à {pluriel(liste.destinataires.length, 'compte', 'comptes')} de la direction.</>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Liste des mois
// ---------------------------------------------------------------------------

function ListeMois({
  liste,
  choisie,
  onChoisir,
}: {
  liste: ListeSyntheses;
  choisie: VueSynthese | null;
  onChoisir: (id: string) => void;
}): JSX.Element {
  return (
    <>
      {/* Sous la tablette paysage, la liste devient un choix : elle ne pousse pas la synthèse sous la ligne de flottaison. */}
      <div className="lg:hidden">
        <ChampFiltre valeur={choisie?.id ?? ''} onChange={onChoisir} etiquette="Synthèse affichée">
          {liste.mois.flatMap((mois) =>
            mois.versions.map((version) => (
              <option key={version.id} value={version.id}>
                {mois.titre} · version {version.version}
                {version.envoi ? '' : ' · non envoyée'}
              </option>
            )),
          )}
        </ChampFiltre>
      </div>
      <nav aria-label="Synthèses par mois" className="hidden min-w-0 space-y-3 lg:block">
        {liste.mois.map((mois) => (
          <div key={mois.mois} className="rounded-2xl bg-white p-3 shadow-card">
            <div className="px-2 pb-2 font-display font-bold text-codialis-navy">{mois.titre}</div>
            <ul className="space-y-1">
              {mois.versions.map((version) => {
                const active = version.id === choisie?.id;
                return (
                  <li key={version.id}>
                    <button
                      type="button"
                      onClick={() => onChoisir(version.id)}
                      aria-current={active ? 'true' : undefined}
                      className={`flex min-h-[44px] w-full flex-col items-start gap-1 rounded-xl px-3 py-2 text-left text-xs transition ${
                        active ? 'bg-codialis-navy text-white' : 'text-codialis-navy hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-semibold">
                        Version {version.version} · {version.origineLibelle.toLowerCase()}
                      </span>
                      <span className={active ? 'text-white/70' : 'text-codialis-gray'}>
                        {version.envoi ? `Envoyée le ${horodatage(version.envoi.le)}` : 'Non envoyée'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </>
  );
}

// ---------------------------------------------------------------------------
// Une synthèse
// ---------------------------------------------------------------------------

function Synthese({ synthese, onChoisir }: { synthese: VueSynthese; onChoisir: (id: string) => void }): JSX.Element {
  const toast = useToast();
  const lue = useSyntheseMensuelle(synthese.id);
  const contenu = lue.data?.contenu;
  const generer = useGenererSynthese();
  const [envoiOuvert, setEnvoiOuvert] = useState(false);

  const nouvelleVersion = (): void => {
    generer.mutate(synthese.mois, {
      onSuccess: (creee) => {
        toast(`Version ${creee.version} de ${creee.titre.toLowerCase()} produite.`);
        onChoisir(creee.id);
      },
      onError: (erreur) => toast(messageErreur(erreur, 'La synthèse n’a pas pu être produite.')),
    });
  };

  return (
    <div className="min-w-0 space-y-6">
      <div className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
        <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-start 2xl:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-xl font-bold text-codialis-navy">
                {synthese.titre} · version {synthese.version}
              </h2>
              <Pastille texte={synthese.origineLibelle} ton={synthese.origine === 'AUTOMATIQUE' ? 'info' : 'neutral'} />
              <Pastille texte={synthese.envoi ? 'Envoyée' : 'Non envoyée'} ton={synthese.envoi ? 'success' : 'warning'} />
            </div>
            <p className="mt-1 text-sm text-codialis-gray">
              Établie le {horodatage(synthese.genereeLe)}, {auteur(synthese)}
              {contenu && (
                <>
                  {' · '}comparée à {contenu.precedent.libelle}
                  {' · '}situations lues au {jourFrancais(contenu.situationAu)}
                </>
              )}
            </p>
            {synthese.envoi && (
              <p className="mt-1 text-xs text-codialis-gray">
                Envoyée le {horodatage(synthese.envoi.le)}
                {synthese.envoi.par ? ` par ${synthese.envoi.par}` : ' automatiquement'} à{' '}
                {synthese.envoi.destinataires.map((destinataire) => destinataire.nom).join(', ') || 'personne'}.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={urlPdfSynthese(synthese.id)}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Télécharger le PDF
            </a>
            <BoutonSecondaire onClick={() => setEnvoiOuvert(true)}>
              <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
              {synthese.envoi ? 'Renvoyer par e-mail' : 'Envoyer par e-mail'}
            </BoutonSecondaire>
            <BoutonSecondaire onClick={nouvelleVersion} disabled={generer.isPending}>
              <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
              {generer.isPending ? 'Production…' : 'Nouvelle version'}
            </BoutonSecondaire>
          </div>
        </div>
        <p className="mt-4 rounded-xl bg-codialis-sand p-3 text-xs text-codialis-gray">
          Chaque chiffre est celui de l’écran qui tient la donnée, lu sur le mois civil et comparé au mois précédent.
          La synthèse est figée à sa génération : une donnée corrigée depuis n’y figure que dans une nouvelle version.
          Agrégats seuls, sans aucun nom.
        </p>
      </div>

      {lue.isLoading && <Chargement libelle="Chargement de la synthèse…" />}
      {lue.error && <MessageErreur message={messageErreur(lue.error, 'Impossible de lire cette synthèse.')} />}
      {contenu?.sections.map((section) => <Section key={section.id} section={section} contenu={contenu} />)}

      {envoiOuvert && <ModaleEnvoi synthese={synthese} onFermer={() => setEnvoiOuvert(false)} />}
    </div>
  );
}

const TONS_ECART: Record<TonEcart, string> = {
  success: 'text-emerald-700',
  danger: 'text-red-700',
  neutral: 'text-codialis-gray',
};

function Tuile({ indicateur }: { indicateur: IndicateurSynthese }): JSX.Element {
  const { comparaison } = indicateur;
  return (
    <div className="min-w-0 rounded-xl border border-gray-100 p-3 sm:p-4">
      <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">{indicateur.libelle}</div>
      <div className="font-display mt-1 break-words text-2xl font-extrabold text-codialis-navy">{indicateur.texte}</div>
      {comparaison && (
        <div className="mt-1 text-xs">
          {comparaison.ecart && <span className={`font-bold ${TONS_ECART[comparaison.ton]}`}>{comparaison.ecart}</span>}
          <span className="text-codialis-gray">
            {comparaison.ecart ? ' · ' : ''}
            {comparaison.texte}
          </span>
        </div>
      )}
      {indicateur.detail && <div className="mt-1 text-xs text-codialis-gray">{indicateur.detail}</div>}
    </div>
  );
}

function Section({ section, contenu }: { section: SectionSynthese; contenu: ContenuSynthese }): JSX.Element {
  return (
    <section aria-labelledby={`section-${section.id}`} className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 id={`section-${section.id}`} className="font-display text-lg font-bold text-codialis-navy">
            {section.titre}
          </h3>
          <p className="mt-0.5 text-xs text-codialis-gray">{section.lecture}</p>
        </div>
        <Link
          to={section.lien.chemin}
          className="inline-flex min-h-[44px] shrink-0 items-center text-sm font-semibold text-codialis-sky hover:underline"
        >
          {section.lien.libelle}
        </Link>
      </div>

      {section.indicateurs.length > 0 && (
        <>
          <h4 className="mt-4 text-[11px] font-bold uppercase tracking-wider text-codialis-navy">
            Sur {contenu.libelle}
          </h4>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {section.indicateurs.map((indicateur) => (
              <Tuile key={indicateur.id} indicateur={indicateur} />
            ))}
          </div>
        </>
      )}

      {section.situation.length > 0 && (
        <>
          <h4 className="mt-5 text-[11px] font-bold uppercase tracking-wider text-codialis-navy">
            Au {jourFrancais(contenu.situationAu)}
          </h4>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {section.situation.map((indicateur) => (
              <Tuile key={indicateur.id} indicateur={indicateur} />
            ))}
          </div>
        </>
      )}

      {section.tableaux.map((tableau) => (
        <Tableau key={tableau.id} tableau={tableau} />
      ))}
    </section>
  );
}

/** Un tableau sur tablette et ordinateur ; une fiche par ligne sur téléphone. */
function Tableau({ tableau }: { tableau: TableauSynthese }): JSX.Element {
  const [premiere, ...autres] = tableau.colonnes;
  return (
    <div className="mt-5">
      <h4 className="text-[11px] font-bold uppercase tracking-wider text-codialis-navy">{tableau.titre}</h4>
      {tableau.lignes.length === 0 ? (
        <p className="mt-2 text-sm text-codialis-gray">{tableau.vide}</p>
      ) : (
        <>
          <ul className="mt-2 space-y-2 sm:hidden">
            {tableau.lignes.map((ligne, rang) => (
              <li
                key={rang}
                className={`rounded-xl border border-gray-100 p-3 ${ligne.total ? 'bg-codialis-sand' : ''}`}
              >
                <div className="text-sm font-semibold text-codialis-navy">{ligne.cellules[0]}</div>
                <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {autres.map((colonne, index) => (
                    <div key={colonne.libelle} className="contents">
                      <dt className="text-codialis-gray">{colonne.libelle}</dt>
                      <dd className="text-right font-medium text-codialis-navy">{ligne.cellules[index + 1] || '—'}</dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>
          <div className="mt-2 hidden overflow-x-auto sm:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {[premiere, ...autres].map((colonne) =>
                    colonne ? (
                      <th
                        key={colonne.libelle}
                        scope="col"
                        className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray first:pl-0 ${
                          colonne.alignement === 'droite' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {colonne.libelle}
                      </th>
                    ) : null,
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {tableau.lignes.map((ligne, rang) => (
                  <tr key={rang} className={ligne.total ? 'font-semibold' : ''}>
                    {ligne.cellules.map((cellule, index) => (
                      <td
                        key={index}
                        className={`px-3 py-2 text-codialis-navy first:pl-0 ${
                          tableau.colonnes[index]?.alignement === 'droite' ? 'whitespace-nowrap text-right' : ''
                        }`}
                      >
                        {cellule}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gestes
// ---------------------------------------------------------------------------

function ModaleEnvoi({ synthese, onFermer }: { synthese: VueSynthese; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const liste = useSynthesesMensuelles();
  const envoyer = useEnvoyerSynthese();
  const destinataires = liste.data?.destinataires ?? [];

  const confirmer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    envoyer.mutate(synthese.id, {
      onSuccess: (resultat) => {
        toast(`Synthèse envoyée à ${pluriel(resultat.prevenus, 'compte', 'comptes')} de la direction.`);
        onFermer();
      },
    });
  };

  return (
    <Modale
      titre={`Envoyer la synthèse de ${synthese.titre.toLowerCase()}`}
      sousTitre={`Version ${synthese.version}, par e-mail et dans l’application`}
      ouverte
      onFermer={onFermer}
    >
      <form onSubmit={confirmer} className="space-y-4 p-4 sm:p-6">
        <div>
          <p className="text-sm text-codialis-navy">Elle part aux comptes actifs de la direction :</p>
          <ul className="mt-2 space-y-1 text-sm">
            {destinataires.map((destinataire) => (
              <li key={destinataire.email} className="break-words text-codialis-navy">
                {destinataire.nom} <span className="text-codialis-gray">· {destinataire.email}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-codialis-gray">
          L’e-mail donne les chiffres clés et le lien vers la synthèse ; il ne contient aucun nom d’apprenant.
        </p>
        {envoyer.error && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageErreur(envoyer.error, 'La synthèse n’a pas pu être envoyée.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={envoyer.isPending || destinataires.length === 0}>
            {envoyer.isPending ? 'Envoi…' : 'Envoyer'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}

function ModaleGeneration({
  liste,
  onFermer,
  onGeneree,
}: {
  liste: ListeSyntheses;
  onFermer: () => void;
  onGeneree: (synthese: VueSynthese) => void;
}): JSX.Element {
  const toast = useToast();
  const generer = useGenererSynthese();
  const [mois, setMois] = useState(liste.generables[0]?.mois ?? '');
  useEffect(() => {
    if (!mois && liste.generables[0]) setMois(liste.generables[0].mois);
  }, [liste.generables, mois]);

  const confirmer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    generer.mutate(mois, {
      onSuccess: (creee) => {
        toast(`Synthèse de ${creee.titre.toLowerCase()} produite (version ${creee.version}).`);
        onGeneree(creee);
      },
    });
  };

  return (
    <Modale
      titre="Générer une synthèse"
      sousTitre="Un mois terminé, parmi les douze derniers : la synthèse est produite sans être envoyée"
      ouverte
      onFermer={onFermer}
    >
      <form onSubmit={confirmer} className="space-y-4 p-4 sm:p-6">
        <label className="block">
          <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Mois</span>
          <select
            value={mois}
            onChange={(evenement) => setMois(evenement.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy"
          >
            {liste.generables.map((option) => (
              <option key={option.mois} value={option.mois}>
                {option.titre}
                {option.versions > 0 ? ` · ${pluriel(option.versions, 'version existante', 'versions existantes')}` : ''}
              </option>
            ))}
          </select>
        </label>
        <p className="text-xs text-codialis-gray">
          Les chiffres du mois sont relus aujourd’hui ; les situations — apprenants en formation et à risque, reste dû,
          retards — sont celles du jour. Une synthèse existante n’est pas modifiée : celle-ci s’ajoute en nouvelle
          version.
        </p>
        {generer.error && (
          <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800">
            {messageErreur(generer.error, 'La synthèse n’a pas pu être produite.')}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={generer.isPending || !mois}>
            {generer.isPending ? 'Production…' : 'Générer'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}
